import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate, useParams, useLocation, NavLink } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { api } from '../context/AuthContext';

const S = {
  card: { background: '#fff', borderRadius: '16px', padding: '1.5rem', border: '1px solid #f1f5f9', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  statCard: { background: '#f8fafc', borderRadius: '12px', padding: '1rem 1.25rem', border: '1px solid #e2e8f0' },
  badge: (color) => ({
    display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
    ...(color === 'green'  && { background: '#dcfce7', color: '#166534' }),
    ...(color === 'yellow' && { background: '#fef9c3', color: '#854d0e' }),
    ...(color === 'red'    && { background: '#fee2e2', color: '#991b1b' }),
    ...(color === 'gray'   && { background: '#f1f5f9', color: '#475569' }),
    ...(color === 'blue'   && { background: '#dbeafe', color: '#1e40af' }),
    ...(color === 'orange' && { background: '#ffedd5', color: '#9a3412' }),
  }),
  btn: (v = 'primary') => ({
    padding: '8px 16px', borderRadius: '10px', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: '600', fontFamily: "'Sora', sans-serif",
    ...(v === 'primary' && { background: '#F97316', color: '#fff' }),
    ...(v === 'outline' && { background: '#fff', color: '#374151', border: '1px solid #e2e8f0' }),
    ...(v === 'warning' && { background: '#f59e0b', color: '#fff', border: 'none' }),
    ...(v === 'danger'  && { background: '#fee2e2', color: '#991b1b', border: 'none' }),
    ...(v === 'success' && { background: '#dcfce7', color: '#166534', border: 'none' }),
  }),
  inp: {
    width: '100%', padding: '10px 14px', borderRadius: '10px', boxSizing: 'border-box',
    border: '1px solid #e2e8f0', fontSize: '14px', fontFamily: "'Sora', sans-serif",
    marginBottom: '12px', outline: 'none', color: '#1e293b', background: '#fff',
  },
};

const fmt = (n) => `₹${parseFloat(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const statusColor = { ACTIVE: 'blue', OVERDUE: 'red', PAID: 'green', DEFAULTED: 'gray', PENDING: 'yellow', APPROVED: 'green', REJECTED: 'red' };

// ─── LAYOUT ───────────────────────────────────────────────────────────────────
function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get('/notifications').then(r => {
      setUnread(r.data.notifications?.filter(n => !n.is_read).length || 0);
    }).catch(() => {});
  }, []);

  const navItems = [
    { to: '/admin',               label: '📊 Dashboard',    end: true },
    { to: '/admin/customers',     label: '👥 Customers'              },
    { to: '/admin/loans',         label: '💰 Loans'                  },
    { to: '/admin/links',         label: '🔗 Invite Links'           },
    { to: '/admin/notifications', label: `🔔 Alerts${unread > 0 ? ` (${unread})` : ''}` },
    { to: '/admin/profile',       label: '👤 My Profile'             },
  ];

  const isMobile = window.innerWidth <= 768;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', fontFamily: "'Sora', sans-serif", background: '#f8fafc' }}>

      {/* Mobile Top Bar */}
      {isMobile && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, background: '#0f172a', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '30px', height: '30px', borderRadius: '8px', background: 'linear-gradient(135deg,#F97316,#EF4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>₹</div>
            <div style={{ color: '#fff', fontWeight: '700', fontSize: '15px' }}>Udhari</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {unread > 0 && <span style={{ background: '#F97316', color: '#fff', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700' }}>{unread}</span>}
            <button onClick={() => setMenuOpen(!menuOpen)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: '22px', cursor: 'pointer', padding: '4px' }}>
              {menuOpen ? '✕' : '☰'}
            </button>
          </div>
        </div>
      )}

      {/* Mobile Drawer */}
      {isMobile && menuOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99 }}>
          <div onClick={() => setMenuOpen(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }} />
          <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '260px', background: '#0f172a', padding: '4rem 1rem 1rem', overflowY: 'auto' }}>
            <div style={{ background: 'rgba(249,115,22,0.1)', borderRadius: '12px', padding: '12px', marginBottom: '1.5rem', border: '1px solid rgba(249,115,22,0.2)' }}>
              <div style={{ color: '#F97316', fontWeight: '600', fontSize: '13px' }}>{user?.shop_name || user?.name}</div>
              <div style={{ color: '#64748b', fontSize: '11px' }}>{user?.city}</div>
            </div>
            <nav>
              {navItems.map(item => (
                <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setMenuOpen(false)} style={({ isActive }) => ({
                  display: 'block', padding: '12px 14px', borderRadius: '10px',
                  color: isActive ? '#F97316' : '#94a3b8', textDecoration: 'none',
                  fontSize: '14px', fontWeight: '500', marginBottom: '4px',
                  background: isActive ? 'rgba(249,115,22,0.1)' : 'transparent',
                })}>
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <button onClick={() => { logout(); navigate('/login'); }}
              style={{ ...S.btn('outline'), background: 'rgba(255,255,255,0.05)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)', width: '100%', marginTop: '1rem' }}>
              🚪 Logout
            </button>
          </div>
        </div>
      )}

      {/* Desktop Sidebar */}
      {!isMobile && (
        <div style={{ width: '220px', background: '#0f172a', display: 'flex', flexDirection: 'column', padding: '1.5rem 1rem', position: 'fixed', height: '100vh', zIndex: 10 }}>
          <div style={{ marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #F97316, #EF4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>₹</div>
            <div>
              <div style={{ color: '#fff', fontWeight: '700', fontSize: '16px' }}>Udhari</div>
              <div style={{ color: '#94a3b8', fontSize: '11px' }}>Admin Panel</div>
            </div>
          </div>
          <div style={{ background: 'rgba(249,115,22,0.1)', borderRadius: '12px', padding: '12px', marginBottom: '1.5rem', border: '1px solid rgba(249,115,22,0.2)' }}>
            <div style={{ color: '#F97316', fontWeight: '600', fontSize: '13px' }}>{user?.shop_name || user?.name}</div>
            <div style={{ color: '#64748b', fontSize: '11px' }}>{user?.city}</div>
          </div>
          <nav style={{ flex: 1 }}>
            {navItems.map(item => (
              <NavLink key={item.to} to={item.to} end={item.end} style={({ isActive }) => ({
                display: 'block', padding: '10px 12px', borderRadius: '10px',
                color: isActive ? '#F97316' : '#94a3b8', textDecoration: 'none',
                fontSize: '13px', fontWeight: '500', marginBottom: '4px',
                background: isActive ? 'rgba(249,115,22,0.1)' : 'transparent',
              })}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <button onClick={() => { logout(); navigate('/login'); }}
            style={{ ...S.btn('outline'), background: 'rgba(255,255,255,0.05)', color: '#94a3b8', border: '1px solid rgba(255,255,255,0.1)', width: '100%' }}>
            🚪 Logout
          </button>
        </div>
      )}

      <div style={{ marginLeft: isMobile ? '0' : '220px', flex: 1, padding: isMobile ? '4.5rem 1rem 1rem' : '2rem', minHeight: '100vh', background: 'transparent' }}>{children}</div>
    </div>
  );
}

// ─── DASHBOARD HOME ───────────────────────────────────────────────────────────
function DashHome() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/admin/dashboard').then(r => setData(r.data.dashboard)).catch(() => toast.error('Failed to load'));
  }, []);

  if (!data) return <div style={{ color: '#94a3b8', padding: '2rem' }}>Loading dashboard...</div>;
  const { stats, recent_loans, overdue_customers, recent_repayments } = data;

  return (
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: '0 0 2rem' }}>Dashboard Overview</h1>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {[
          { label: 'Total Customers', value: stats.total_customers || 0, icon: '👥', link: '/admin/customers',                        sub: 'All registered borrowers',   color: '#818cf8' },
          { label: 'Active Loans',    value: stats.active_loans || 0,   icon: '💰', link: '/admin/loans',    state: { filter: 'ACTIVE' },   sub: 'Currently running loans',    color: '#fb923c' },
          { label: 'Overdue Loans',   value: stats.overdue_loans || 0,  icon: '⚠️', link: '/admin/loans',    state: { filter: 'OVERDUE' },  sub: 'Requires attention',          color: '#f87171', alert: parseInt(stats.overdue_loans) > 0 },
          { label: 'Total Lent',      value: fmt(stats.total_lent),     icon: '📤', link: '/admin/loans',    state: { filter: 'ALL' },      sub: 'Cumulative disbursement',    color: '#34d399' },
          { label: 'Recovered',       value: fmt(stats.total_recovered),icon: '✅', link: '/admin/loans',    state: { filter: 'PAID' },     sub: 'Successfully collected',     color: '#4ade80' },
          { label: 'Pending',         value: fmt(stats.total_pending),  icon: '🕐', link: '/admin/loans',    state: { filter: 'ACTIVE' },   sub: 'Outstanding balance',        color: '#fbbf24', alert: parseFloat(stats.total_pending) > 0 },
        ].map((s, i) => (
          <div key={i} onClick={() => navigate(s.link, { state: s.state })}
            style={{ ...S.statCard, borderColor: s.alert ? '#fca5a5' : '#e2e8f0', background: s.alert ? '#fff5f5' : '#f8fafc', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ fontSize: '26px' }}>{s.icon}</div>
              <div style={{ fontSize: '10px', color: s.color, fontWeight: '600', background: `${s.color}22`, padding: '3px 8px', borderRadius: '20px' }}>View →</div>
            </div>
            <div style={{ fontSize: '26px', fontWeight: '800', color: s.alert ? '#dc2626' : '#0f172a' }}>{s.value}</div>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#374151', marginTop: '2px' }}>{s.label}</div>
            <div style={{ fontSize: '11px', color: '#475569', marginTop: '2px' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', margin: 0 }}>Recent Loans</h3>
            <button onClick={() => navigate('/admin/loans')} style={{ fontSize: '11px', color: '#F97316', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}>View All →</button>
          </div>
          {!recent_loans?.length ? <p style={{ color: '#64748b', fontSize: '13px' }}>No loans yet.</p>
            : recent_loans.map(l => (
              <div key={l.id} 
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 8px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', borderRadius: '8px' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>{l.customer_name}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>Due: {fmtDate(l.due_date)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: '600', fontSize: '13px' }}>{fmt(l.loan_amount)}</div>
                  <span style={S.badge(statusColor[l.status] || 'gray')}>{l.status}</span>
                </div>
              </div>
            ))}
        </div>

        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#dc2626', margin: 0 }}>⚠️ Overdue Customers</h3>
            <button onClick={() => navigate('/admin/customers')} style={{ fontSize: '11px', color: '#F97316', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}>View All →</button>
          </div>
          {!overdue_customers?.length ? <p style={{ color: '#94a3b8', fontSize: '13px' }}>No overdue loans. Great!</p>
            : overdue_customers.map(c => (
              <div key={c.id}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 8px', borderBottom: '1px solid #f1f5f9', borderRadius: '8px' }}
              >
                <div>
                  <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>{c.full_name}</div>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{c.phone} · {c.overdue_count} loan(s)</div>
                </div>
                <div style={{ fontWeight: '700', color: '#dc2626', fontSize: '14px' }}>{fmt(c.total_due)}</div>
              </div>
            ))}
        </div>
      </div>

      {/* Recent Repayments */}
      <div style={{ ...S.card, marginTop: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', margin: 0 }}>💳 Recent Repayments</h3>
          <button onClick={() => navigate('/admin/loans')} style={{ fontSize: '11px', color: '#F97316', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600' }}>View All →</button>
        </div>
        {!recent_repayments?.length ? (
          <p style={{ color: '#94a3b8', fontSize: '13px' }}>No repayments recorded yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                {['Customer', 'Amount Paid', 'Mode', 'Date'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#475569', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recent_repayments.map((r, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px 12px', fontWeight: '600', color: '#1e293b' }}>{r.customer_name}</td>
                  <td style={{ padding: '10px 12px', fontWeight: '700', color: '#16a34a' }}>{fmt(r.amount_paid)}</td>
                  <td style={{ padding: '10px 12px' }}><span style={S.badge('blue')}>{r.payment_mode}</span></td>
                  <td style={{ padding: '10px 12px', color: '#64748b' }}>{fmtDate(r.payment_date)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

// ─── CUSTOMERS ────────────────────────────────────────────────────────────────
function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [kycModal, setKycModal] = useState(null); // customer object
  const [platformHistory, setPlatformHistory] = React.useState([]);

  const openKycModal = async (c) => {
    setKycModal(c);
    try {
      const t = localStorage.getItem('udhari_token');
      const apiBase = process.env.REACT_APP_API_URL || 'https://api-udhari.arena19.in/api';
      const r = await fetch(`${apiBase}/customers/${c.id}/platform-history`, { headers: { Authorization: `Bearer ${t}` } });
      const d = await r.json();
      if (d.success) setPlatformHistory(d.history.filter(h => h.id !== c.id));
      else setPlatformHistory([]);
    } catch { setPlatformHistory([]); }
  };

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = filter === 'ALL' ? '' : filter;
      const r = await api.get(`/admin/customers${status ? `?status=${status}` : ''}`);
      setCustomers(r.data.customers || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id, action) => {
    try {
      await api.put(`/admin/customers/${id}/approve`, { action });
      toast.success(`Customer ${action.toLowerCase()}`);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleKycVerify = async (id, verified) => {
    try {
      await api.put(`/admin/customers/${id}/kyc`, { verified });
      toast.success(verified ? '✅ KYC Verified' : 'KYC Unverified');
      setCustomers(cs => cs.map(c => c.id === id ? { ...c, kyc_verified: verified ? 1 : 0 } : c));
      if (kycModal?.id === id) setKycModal(prev => ({ ...prev, kyc_verified: verified ? 1 : 0 }));
    } catch (err) { toast.error('Failed to update KYC'); }
  };

  const kycUrl = (filename) => {
    if (!filename) return null;
    const apiBase = process.env.REACT_APP_API_URL || 'https://api-udhari.arena19.in/api';
    const t = localStorage.getItem('udhari_token');
    return `${apiBase}/kyc/${filename}?token=${t}`;
  };

  return (
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: '0 0 1.5rem' }}>Customers</h1>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '1.5rem' }}>
        {['ALL', 'PENDING', 'APPROVED', 'REJECTED', 'DEFAULTED'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ ...S.btn(filter === f ? 'primary' : 'outline'), fontSize: '12px' }}>{f}</button>
        ))}
      </div>
      {loading ? <div style={{ color: '#94a3b8' }}>Loading...</div> : (
        <div style={S.card}>
          <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                {['Name', 'Phone', 'Occupation', 'Trust Score', 'Outstanding', 'KYC', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#475569', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!customers.length ? (
                <tr><td colSpan={8} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No customers found</td></tr>
              ) : customers.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px', fontWeight: '600', color: '#1e293b' }}>{c.full_name}</td>
                  <td style={{ padding: '12px', color: '#64748b' }}>{c.phone}</td>
                  <td style={{ padding: '12px' }}><span style={S.badge('gray')}>{c.occupation_type}</span></td>
                  <td style={{ padding: '12px', fontWeight: '700', color: c.trust_score >= 80 ? '#16a34a' : c.trust_score >= 50 ? '#ca8a04' : '#dc2626' }}>
                    {c.trust_score}
                  </td>
                  <td style={{ padding: '12px', fontWeight: '600', color: parseFloat(c.total_outstanding) > 0 ? '#dc2626' : '#16a34a' }}>{fmt(c.total_outstanding)}</td>
                  <td style={{ padding: '12px' }}>
                    {c.kyc_aadhaar_front ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <button onClick={() => openKycModal(c)} style={{ ...S.btn('outline'), fontSize: '11px', padding: '4px 8px' }}>👁 View KYC</button>
                        <span style={S.badge(c.kyc_verified ? 'green' : 'yellow')}>{c.kyc_verified ? '✓ Verified' : 'Pending'}</span>
                      </div>
                    ) : <span style={{ color: '#94a3b8', fontSize: '11px' }}>No docs</span>}
                  </td>
                  <td style={{ padding: '12px' }}><span style={S.badge(statusColor[c.status] || 'gray')}>{c.status}</span></td>
                  <td style={{ padding: '12px' }}>
                    {c.status === 'PENDING' && (
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={() => handleApprove(c.id, 'APPROVED')} style={{ ...S.btn('success'), fontSize: '11px', padding: '5px 10px' }}>✓ Approve</button>
                        <button onClick={() => handleApprove(c.id, 'REJECTED')} style={{ ...S.btn('danger'), fontSize: '11px', padding: '5px 10px' }}>✗ Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {/* KYC Modal */}
      {kycModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ background: '#fff', borderRadius: '20px', padding: '2rem', maxWidth: '700px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>KYC Documents — {kycModal.full_name}</h2>
              <button onClick={() => setKycModal(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              {[['Aadhaar Front', kycModal.kyc_aadhaar_front], ['Aadhaar Back', kycModal.kyc_aadhaar_back], ['PAN Card', kycModal.kyc_pan_card]].map(([label, filename]) => (
                <div key={label}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#64748b', marginBottom: '6px', textTransform: 'uppercase' }}>{label}</div>
                  {filename ? (
                    <a href={kycUrl(filename)} target="_blank" rel="noreferrer">
                      <img src={kycUrl(filename)} alt={label} style={{ width: '100%', borderRadius: '10px', border: '1px solid #e2e8f0', objectFit: 'cover', height: '140px', cursor: 'pointer' }} onError={e => { e.target.style.display='none'; }} />
                    </a>
                  ) : <div style={{ width: '100%', height: '140px', borderRadius: '10px', border: '2px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '12px' }}>Not uploaded</div>}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <div style={{ marginRight: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>Status:</span>
                <span style={S.badge(kycModal.kyc_verified ? 'green' : 'yellow')}>{kycModal.kyc_verified ? '✓ Verified' : 'Pending Verification'}</span>
              </div>
              {!kycModal.kyc_verified ? (
                <button onClick={() => handleKycVerify(kycModal.id, true)} style={S.btn('success')}>✓ Verify KYC</button>
              ) : (
                <button onClick={() => handleKycVerify(kycModal.id, false)} style={S.btn('danger')}>✗ Revoke Verification</button>
              )}
              <button onClick={() => setKycModal(null)} style={S.btn('outline')}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── LOANS ────────────────────────────────────────────────────────────────────
function Loans() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showRepay, setShowRepay] = useState(null);
  const [form, setForm] = useState({ customer_id: '', loan_amount: '', due_date: '', purpose: '', penalty_rate: '2', penalty_type: 'WEEKLY', notes: '' });
  const [repayForm, setRepayForm] = useState({ amount_paid: '', payment_mode: 'CASH', notes: '' });
  const { state: routeState } = useLocation();
  const [filter, setFilter] = useState(routeState?.filter || 'ALL');
  const [upiReqLoading, setUpiReqLoading] = useState(null); // loanId being toggled

  const toggleUpiRequest = async (loan) => {
    const newVal = !loan.upi_payment_requested;
    setUpiReqLoading(loan.id);
    try {
      await api.put(`/loans/${loan.id}/upi-payment-request`, { requested: newVal });
      setLoans(prev => prev.map(l => l.id === loan.id ? { ...l, upi_payment_requested: newVal ? 1 : 0 } : l));
      toast.success(newVal ? '📱 UPI payment request sent to customer!' : 'UPI request cancelled.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update UPI request');
    }
    setUpiReqLoading(null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lr, cr] = await Promise.all([api.get('/loans'), api.get('/admin/customers?status=APPROVED')]);
      setLoans(lr.data.loans || []);
      setCustomers(cr.data.customers || []);
    } catch { toast.error('Failed to load loans'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createLoan = async () => {
    if (!form.customer_id || !form.loan_amount || !form.due_date) return toast.error('Fill all required fields');
    try {
      await api.post('/loans', { ...form, loan_amount: parseFloat(form.loan_amount), penalty_rate: parseFloat(form.penalty_rate) });
      toast.success('Loan created!');
      setShowCreate(false);
      setForm({ customer_id: '', loan_amount: '', due_date: '', purpose: '', penalty_rate: '2', penalty_type: 'WEEKLY', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const recordRepayment = async () => {
    if (!repayForm.amount_paid) return toast.error('Enter amount');
    try {
      const r = await api.post('/loans/repayment', { loan_id: showRepay.id, ...repayForm, amount_paid: parseFloat(repayForm.amount_paid) });
      toast.success(r.data.message);
      setShowRepay(null);
      setRepayForm({ amount_paid: '', payment_mode: 'CASH', notes: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const markDefault = async (id) => {
    if (!window.confirm('Mark as DEFAULTED? Trust score will drop by 40 points.')) return;
    try { await api.put(`/loans/${id}/default`); toast.success('Marked as defaulted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const deleteLoan = async (id, customerName) => {
    if (!window.confirm(`Delete loan for ${customerName}? All repayments will also be deleted. This cannot be undone!`)) return;
    try { await api.delete(`/loans/${id}`); toast.success('Loan deleted'); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const modal = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Loans / Udhari</h1>
        <button onClick={() => setShowCreate(true)} style={S.btn('primary')}>+ New Loan</button>
      </div>

      {loading ? <div style={{ color: '#94a3b8' }}>Loading...</div> : (
        <div style={S.card}>
          <div style={{ overflowX: 'auto' }}><table style={{ width: '100%', minWidth: '600px', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                {['Customer', 'Amount', 'Paid', 'Outstanding', 'Due Date', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#475569', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loans.length ? (
                <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No loans yet.</td></tr>
              ) : loans.map(l => (
                <tr key={l.id} style={{ borderBottom: '1px solid #f1f5f9', background: l.status === 'OVERDUE' ? '#fffbeb' : l.status === 'DEFAULTED' ? '#fff5f5' : '#fff' }}>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>{l.customer_name}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>Score: {l.trust_score}</div>
                  </td>
                  <td style={{ padding: '12px', fontWeight: '600' }}>{fmt(l.loan_amount)}</td>
                  <td style={{ padding: '12px', color: '#16a34a' }}>{fmt(l.amount_paid)}</td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ fontWeight: '700', color: parseFloat(l.outstanding_amount) > 0 ? '#dc2626' : '#16a34a' }}>{fmt(l.outstanding_amount)}</div>
                    {parseFloat(l.calculated_penalty) > 0 && <div style={{ fontSize: '10px', color: '#f97316' }}>+{fmt(l.calculated_penalty)} penalty</div>}
                  </td>
                  <td style={{ padding: '12px', color: '#64748b' }}>
                    {fmtDate(l.due_date)}
                    {parseInt(l.days_overdue) > 0 && <div style={{ fontSize: '10px', color: '#dc2626' }}>{l.days_overdue}d overdue</div>}
                  </td>
                  <td style={{ padding: '12px' }}><span style={S.badge(statusColor[l.status] || 'gray')}>{l.status}</span></td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {['ACTIVE', 'OVERDUE'].includes(l.status) && (
                        <button onClick={() => setShowRepay(l)} style={{ ...S.btn('success'), fontSize: '11px', padding: '5px 10px' }}>💳 Pay</button>
                      )}

                      {l.status === 'OVERDUE' && <>
                        <button onClick={() => markDefault(l.id)} style={{ ...S.btn('danger'), fontSize: '11px', padding: '5px 10px' }}>⛔ Default</button>
                        {parseInt(l.days_overdue) >= 15 ? (
                          <button onClick={() => {
                            const token3 = localStorage.getItem('udhari_token');
                            const apiBase2 = process.env.REACT_APP_API_URL || 'https://api-udhari.arena19.in/api';
                            window.open(`${apiBase2}/loans/${l.id}/legal-notice?token=${token3}`, '_blank');
                          }} style={{ ...S.btn('outline'), fontSize: '11px', padding: '5px 10px' }}>📄 Notice</button>
                        ) : (
                          <span style={{ fontSize: '10px', color: '#94a3b8', padding: '5px' }}>Notice in {15 - parseInt(l.days_overdue)}d</span>
                        )}
              <button onClick={() => {
                const token = localStorage.getItem('udhari_token');
                const apiBase = process.env.REACT_APP_API_URL || 'https://api-udhari.arena19.in/api';
                const token2 = localStorage.getItem('udhari_token');
                window.open(`${apiBase}/loans/${l.id}/receipt?token=${token2}`, '_blank');
              }} style={{ ...S.btn('outline'), fontSize: '11px', padding: '5px 10px' }}>🧾 Receipt</button>
              <button onClick={() => deleteLoan(l.id, l.customer_name)} style={{ ...S.btn('danger'), fontSize: '11px', padding: '5px 10px' }}>🗑️ Delete</button>
                      </>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}

      {/* Create Loan Modal */}
      {showCreate && (
        <div style={modal} onClick={() => setShowCreate(false)}>
          <div onClick={e => e.stopPropagation()} style={{ ...S.card, width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 1.5rem', fontSize: '18px', fontWeight: '700' }}>Create New Loan</h3>
            <select style={S.inp} value={form.customer_id} onChange={e => setForm(f => ({ ...f, customer_id: e.target.value }))}>
              <option value="">Select Customer *</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.full_name} — {c.phone} (Score: {c.trust_score})</option>)}
            </select>
            <input type="number" style={S.inp} placeholder="Loan Amount (₹) *" value={form.loan_amount} onChange={e => setForm(f => ({ ...f, loan_amount: e.target.value }))} />
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Due Date *</div>
            <input type="date" style={S.inp} value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} min={new Date().toISOString().split('T')[0]} />
            <input type="text" style={S.inp} placeholder="Purpose (optional)" value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <select style={{ ...S.inp, flex: 1 }} value={form.penalty_type} onChange={e => setForm(f => ({ ...f, penalty_type: e.target.value }))}>
                <option value="DAILY">Daily Penalty</option>
                <option value="WEEKLY">Weekly Penalty</option>
                <option value="MONTHLY">Monthly Penalty</option>
              </select>
              <input type="number" style={{ ...S.inp, width: '80px' }} placeholder="Rate %" value={form.penalty_rate} onChange={e => setForm(f => ({ ...f, penalty_rate: e.target.value }))} />
            </div>
            <textarea style={{ ...S.inp, height: '60px', resize: 'vertical' }} placeholder="Notes (optional)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreate(false)} style={S.btn('outline')}>Cancel</button>
              <button onClick={createLoan} style={S.btn('primary')}>Create Loan ₹</button>
            </div>
          </div>
        </div>
      )}

      {/* Repayment Modal */}
      {showRepay && (
        <div style={modal} onClick={() => setShowRepay(null)}>
          <div onClick={e => e.stopPropagation()} style={{ ...S.card, width: '100%', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '18px', fontWeight: '700' }}>Record Payment</h3>
            <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 1.5rem' }}>
              {showRepay.customer_name} · Outstanding: <strong style={{ color: '#dc2626' }}>{fmt(showRepay.outstanding_amount)}</strong>
            </p>
            <input type="number" style={S.inp} placeholder="Amount Paid (₹) *" value={repayForm.amount_paid} onChange={e => setRepayForm(f => ({ ...f, amount_paid: e.target.value }))} max={showRepay.outstanding_amount} />
            <select style={S.inp} value={repayForm.payment_mode} onChange={e => setRepayForm(f => ({ ...f, payment_mode: e.target.value }))}>
              <option value="CASH">Cash</option>
              <option value="UPI">UPI</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
              <option value="CHEQUE">Cheque</option>
              <option value="OTHER">Other</option>
            </select>
            {repayForm.payment_mode === 'UPI' && repayForm.amount_paid && showRepay?.upi_id && (
              <div style={{ textAlign: 'center', padding: '12px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0', marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>📱 Scan to Pay via UPI</div>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(`upi://pay?pa=${showRepay.upi_id}&am=${repayForm.amount_paid}&cu=INR&tn=Udhari+Loan+Repayment`)}`}
                  alt="UPI QR"
                  style={{ width: '160px', height: '160px', borderRadius: '8px' }}
                />
                <div style={{ fontSize: '12px', color: '#166534', marginTop: '8px', fontWeight: '600' }}>₹{repayForm.amount_paid} → {showRepay.upi_id}</div>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>Ask customer to scan & pay, then confirm below</div>
              </div>
            )}
            {repayForm.payment_mode === 'UPI' && !showRepay?.upi_id && (
              <div style={{ padding: '10px 14px', background: '#fffbeb', borderRadius: '10px', border: '1px solid #fcd34d', marginBottom: '12px', fontSize: '12px', color: '#92400e' }}>
                ⚠️ Add your UPI ID in My Profile to show QR code
              </div>
            )}
            <input type="text" style={S.inp} placeholder="Notes (optional)" value={repayForm.notes} onChange={e => setRepayForm(f => ({ ...f, notes: e.target.value }))} />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowRepay(null)} style={S.btn('outline')}>Cancel</button>
              <button onClick={recordRepayment} style={S.btn('success')}>Record Payment ✓</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── INVITE LINKS ─────────────────────────────────────────────────────────────
function Links() {
  const [links, setLinks] = useState([]);

  const generate = async () => {
    try {
      const r = await api.post('/admin/links/generate');
      setLinks(l => [r.data, ...l]);
      toast.success('Link generated!');
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Customer Invite Links</h1>
        <button onClick={generate} style={S.btn('primary')}>+ Generate Link</button>
      </div>
      <div style={S.card}>
        <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '1.5rem' }}>
          Generate unique links to share with customers via WhatsApp. Each link is valid for 7 days and can only be used once.
        </p>
        {!links.length ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
            <div style={{ fontSize: '40px', marginBottom: '8px' }}>🔗</div>
            <p>No links yet. Click "Generate Link" to invite customers.</p>
          </div>
        ) : links.map((l, i) => (
          <div key={i} style={{ padding: '14px', borderRadius: '12px', background: '#f8fafc', marginBottom: '10px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <code style={{ fontSize: '12px', color: '#475569', wordBreak: 'break-all', flex: 1 }}>{l.link}</code>
              <button onClick={() => { navigator.clipboard.writeText(l.link); toast.success('Copied!'); }}
                style={{ ...S.btn('outline'), fontSize: '12px', whiteSpace: 'nowrap' }}>📋 Copy</button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Register as my customer on Udhari!\n\nClick this link:\n${l.link}\n\nValid for 7 days only.`)}`}
                target="_blank" rel="noopener noreferrer" title="Share on WhatsApp"
                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', background: '#25D366', flexShrink: 0, textDecoration: 'none' }}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </a>
            </div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>Expires: {fmtDate(l.expires_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── NOTIFICATIONS ────────────────────────────────────────────────────────────
function Notifications() {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/notifications').then(r => { setNotifs(r.data.notifications || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    setNotifs(n => n.map(x => x.id === id ? { ...x, is_read: true } : x));
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setNotifs(n => n.map(x => ({ ...x, is_read: true })));
    toast.success('All marked as read');
  };

  const deleteOne = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/notifications/${id}`);
    setNotifs(n => n.filter(x => x.id !== id));
  };

  const deleteAll = async () => {
    await api.delete('/notifications');
    setNotifs([]);
    toast.success('All notifications cleared');
  };

  const icons = { LOAN_CREATED: '💰', PAYMENT_RECORDED: '✅', LOAN_OVERDUE: '⚠️', ACCOUNT_APPROVED: '🎉', ACCOUNT_REJECTED: '❌', NEW_CUSTOMER: '👤', NEW_ADMIN_REQUEST: '🏪', PAYMENT_INTIMATION: '💳' };

  if (loading) return <div style={{ color: '#94a3b8', padding: '2rem' }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: 0 }}>Notifications</h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          {notifs.some(n => !n.is_read) && (
            <button onClick={markAllRead} style={S.btn('outline')}>✓ Mark All Read</button>
          )}
          {notifs.length > 0 && (
            <button onClick={deleteAll} style={{ ...S.btn('outline'), color: '#ef4444', borderColor: '#fecaca' }}>🗑️ Clear All</button>
          )}
        </div>
      </div>
      {!notifs.length ? (
        <div style={{ ...S.card, textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔔</div>
          <p style={{ color: '#64748b' }}>No notifications yet.</p>
        </div>
      ) : notifs.map(n => (
        <div key={n.id} onClick={() => !n.is_read && markRead(n.id)}
          style={{ ...S.card, marginBottom: '10px', background: n.is_read ? '#fff' : '#eff6ff', border: n.is_read ? '1px solid #f1f5f9' : '1px solid #bfdbfe', cursor: !n.is_read ? 'pointer' : 'default' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '22px' }}>{icons[n.type] || '📢'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', fontSize: '14px', color: '#1e293b' }}>{n.title}</div>
              <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px' }}>{n.message}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>{new Date(n.created_at).toLocaleString('en-IN')}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
              {!n.is_read && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6' }} />}
              <button onClick={(e) => deleteOne(e, n.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', color: '#94a3b8', padding: '2px 4px', lineHeight: 1 }} title="Delete">🗑️</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── PROFILE ──────────────────────────────────────────────────────────────────
// ─── CUSTOMER PROFILE ─────────────────────────────────────────────────────────
function CustomerProfile() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/admin/customers/${customerId}`),
      api.get(`/admin/loans?customer_id=${customerId}`),
    ]).then(([cr, lr]) => {
      setCustomer(cr.data.customer);
      setLoans(lr.data.loans || []);
      setLoading(false);
    }).catch(() => { toast.error('Failed to load'); setLoading(false); });
  }, [customerId]);

  if (loading) return <div style={{ color: '#94a3b8', padding: '2rem' }}>Loading...</div>;
  if (!customer) return <div style={{ color: '#dc2626', padding: '2rem' }}>Customer not found.</div>;

  const trustColor = customer.trust_score >= 80 ? '#16a34a' : customer.trust_score >= 50 ? '#ca8a04' : '#dc2626';
  const trustBg    = customer.trust_score >= 80 ? '#f0fdf4' : customer.trust_score >= 50 ? '#fefce8' : '#fff5f5';

  return (
    <div>
      {/* Back button */}
      <button onClick={() => navigate('/admin/customers')} style={{ background: 'none', border: 'none', color: '#F97316', cursor: 'pointer', fontSize: '13px', fontWeight: '600', marginBottom: '1.5rem', padding: 0 }}>
        ← Back to Customers
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
        {/* Profile Card */}
        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg,#F97316,#EF4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '22px', fontWeight: '800' }}>
              {customer.full_name?.[0]?.toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: '800', fontSize: '18px', color: '#1e293b' }}>{customer.full_name}</div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>{customer.phone}</div>
            </div>
            <span style={{ ...S.badge(customer.status === 'APPROVED' ? 'green' : customer.status === 'PENDING' ? 'yellow' : 'red'), marginLeft: 'auto' }}>{customer.status}</span>
          </div>

          {/* Trust Score */}
          <div style={{ background: trustBg, borderRadius: '12px', padding: '1rem', marginBottom: '1rem', border: `1px solid ${trustColor}33` }}>
            <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '6px' }}>TRUST SCORE</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ fontSize: '36px', fontWeight: '900', color: trustColor }}>{customer.trust_score}</div>
              <div style={{ flex: 1 }}>
                <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${customer.trust_score}%`, background: trustColor, borderRadius: '4px', transition: 'width 0.5s' }} />
                </div>
                <div style={{ fontSize: '11px', color: trustColor, marginTop: '4px', fontWeight: '600' }}>
                  {customer.trust_score >= 80 ? '🟢 Trustworthy' : customer.trust_score >= 50 ? '🟡 Moderate' : '🔴 High Risk'}
                </div>
              </div>
            </div>
          </div>

          {/* Details */}
          {[
            { label: 'Age',          value: customer.age },
            { label: 'Address',      value: customer.current_address },
            { label: 'Pincode',      value: customer.current_pincode },
            { label: 'Occupation',   value: customer.occupation_type },
            { label: 'Aadhaar',      value: customer.aadhaar_number ? `XXXX XXXX ${customer.aadhaar_number.slice(-4)}` : '—' },
            { label: 'PAN',          value: customer.pan_number || '—' },
            { label: 'GSTIN',        value: customer.gstin || '—' },
            { label: 'Member Since', value: fmtDate(customer.created_at) },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
              <span style={{ color: '#64748b', fontWeight: '600' }}>{item.label}</span>
              <span style={{ color: '#1e293b', fontWeight: '500', textAlign: 'right', maxWidth: '60%' }}>{item.value}</span>
            </div>
          ))}
        </div>

        {/* Loans Card */}
        <div style={S.card}>
          <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: '0 0 1rem' }}>Loan History ({loans.length})</h3>
          {!loans.length ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>💰</div>
              <div>No loans yet</div>
            </div>
          ) : loans.map(l => (
            <div key={l.id} style={{ padding: '12px', borderRadius: '10px', background: '#f8fafc', marginBottom: '8px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontWeight: '700', fontSize: '16px', color: '#1e293b' }}>{fmt(l.loan_amount)}</div>
                <span style={S.badge(statusColor[l.status] || 'gray')}>{l.status}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
                <div>Lent: {fmtDate(l.date_of_lending)}</div>
                <div>Due: {fmtDate(l.due_date)}</div>
                <div>Paid: <strong style={{ color: '#16a34a' }}>{fmt(l.amount_paid)}</strong></div>
                <div>Outstanding: <strong style={{ color: '#dc2626' }}>{fmt(l.outstanding_amount)}</strong></div>
              </div>
              {l.purpose && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '6px' }}>Purpose: {l.purpose}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Profile() {
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', upi_id: '' });

  const load = () => {
    setError('');
    api.get('/admin/profile').then(r => {
      setProfile(r.data.profile);
      setForm({ full_name: r.data.profile.full_name, email: r.data.profile.email || '', phone: r.data.profile.phone, upi_id: r.data.profile.upi_id || '' });
    }).catch(err => {
      const msg = err?.response?.data?.message || 'Failed to load profile';
      setError(msg);
      toast.error(msg);
    });
  };

  useEffect(() => { load(); }, []);

  if (error) return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <div style={{ color: '#ef4444', fontSize: '18px', marginBottom: '12px' }}>⚠️ {error}</div>
      <button onClick={load} style={{ padding: '8px 20px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
        Retry
      </button>
    </div>
  );

  if (!profile) return <div style={{ color: '#94a3b8', padding: '2rem' }}>Loading...</div>;

  const inpS = { ...S.inp, marginBottom: '12px' };
  const lbl = { display: 'block', fontSize: '12px', fontWeight: '600', color: '#64748b', marginBottom: '4px' };

  return (
    <div>
      <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', margin: '0 0 2rem' }}>My Profile</h1>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>

        {/* Shop Info */}
        <div style={S.card}>
          <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', margin: '0 0 1.5rem' }}>🏪 Shop Information</h3>
          {[
            { label: 'Shop Name',     value: profile.shop_name },
            { label: 'Category',      value: profile.shop_category },
            { label: 'City',          value: profile.city },
            { label: 'Pincode',       value: profile.pincode },
            { label: 'GSTIN',         value: profile.gstin || '—', highlight: !!profile.gstin },
            { label: 'Shop Reg. No.', value: profile.shop_registration_number || '—' },
            { label: 'Full Address',  value: profile.full_address },
            { label: 'UPI ID',        value: profile.upi_id || '—', highlight: !!profile.upi_id },
          ].map((item, i) => (
            <div key={i} style={{ padding: '10px 0', borderBottom: i < 7 ? '1px solid #f1f5f9' : 'none' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>{item.label}</div>
              <div style={{ fontSize: '14px', fontWeight: '500', color: item.highlight ? '#16a34a' : '#1e293b', fontFamily: item.highlight ? 'monospace' : 'inherit' }}>
                {item.value}
              </div>
            </div>
          ))}
        </div>

        {/* Personal Info */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#0f172a', margin: 0 }}>👤 Personal Info</h3>
            {!editing && <button onClick={() => setEditing(true)} style={S.btn('primary')}>✏️ Edit</button>}
          </div>

          {!editing ? (
            [
              { label: 'Full Name',  value: profile.full_name },
              { label: 'Phone',      value: profile.phone },
              { label: 'Email',      value: profile.email || '—' },
              { label: 'Age',        value: `${profile.age} years` },
              { label: 'Aadhaar',    value: profile.aadhaar_number ? `${profile.aadhaar_number.slice(0,4)}-XXXX-${profile.aadhaar_number.slice(-4)}` : '—' },
              { label: 'PAN',        value: profile.pan_number || '—' },
              { label: 'Status',     value: profile.status },
              { label: 'Member Since', value: new Date(profile.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' }) },
            ].map((item, i) => (
              <div key={i} style={{ padding: '10px 0', borderBottom: i < 7 ? '1px solid #f1f5f9' : 'none' }}>
                <div style={{ fontSize: '11px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '2px' }}>{item.label}</div>
                <div style={{ fontSize: '14px', fontWeight: '500', color: '#1e293b' }}>{item.value}</div>
              </div>
            ))
          ) : (
            <div>
              <label style={lbl}>Full Name</label>
              <input style={inpS} value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} />
              <label style={lbl}>Email</label>
              <input style={inpS} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              <label style={lbl}>UPI ID (for customer payments)</label>
              <input style={inpS} placeholder="e.g. shop@upi or 9876543210@paytm" value={form.upi_id} onChange={e => setForm(f => ({ ...f, upi_id: e.target.value }))} />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                <button onClick={() => setEditing(false)} style={{ ...S.btn('outline'), flex: 1 }}>Cancel</button>
                <button disabled={loading} onClick={async () => {
                  setLoading(true);
                  try {
                    await api.put('/admin/profile', form);
                    toast.success('Profile updated!');
                    setEditing(false);
                    load();
                  } catch { toast.error('Failed to update'); }
                  finally { setLoading(false); }
                }} style={{ ...S.btn('primary'), flex: 2 }}>
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── EXPORT ───────────────────────────────────────────────────────────────────
export default function AdminDashboard() {
  return (
    <Layout>
      <Routes>
        <Route index element={<DashHome />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/:customerId" element={<CustomerProfile />} />
        <Route path="loans" element={<Loans />} />
        <Route path="links" element={<Links />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="profile" element={<Profile />} />
      </Routes>
    </Layout>
  );
}
